<template>
  <div class="ai-insights-widget">
    <!-- Floating button -->
    <button class="ai-fab" @click="toggle" :title="'תובנות AI'">
      <i :class="open ? 'fa-solid fa-xmark' : 'fa-solid fa-wand-magic-sparkles'"></i>
    </button>

    <!-- Panel -->
    <div v-if="open" class="ai-panel shadow" dir="rtl">
      <div class="ai-panel-head">
        <strong><i class="fa-solid fa-wand-magic-sparkles me-1"></i> תובנות חכמות על האירועים</strong>
        <button v-if="messages.length" type="button" class="ai-new-btn" :disabled="streaming" @click="newConversation" title="להתחיל שיחה חדשה">
          <i class="fa-regular fa-pen-to-square"></i> שיחה חדשה
        </button>
      </div>

      <div class="ai-panel-body" ref="body">
        <div v-if="!messages.length" class="ai-suggestions">
          <div class="text-muted mb-2" style="font-size:13px">{{ hasCatalog ? 'שאלו אותי כל דבר על האירועים והקטלוג שלכם.' : 'שאלו אותי כל דבר על האירועים שלכם.' }} התשובות מחושבות מהנתונים שלכם, ואפשר להמשיך בשאלות המשך.</div>
          <button v-for="s in suggestions" :key="s" class="btn btn-outline-secondary btn-sm ai-sugg" @click="useSuggestion(s)">{{ s }}</button>
        </div>

        <div v-for="(m, i) in messages" :key="i" class="ai-msg" :class="m.role === 'user' ? 'ai-msg-user' : 'ai-msg-assistant'">
          <span style="white-space:pre-wrap">{{ m.text }}</span><span v-if="m.role === 'assistant' && streaming && i === messages.length - 1 && m.text" class="ai-caret">▋</span>
        </div>

        <div v-if="streaming && status" class="ai-status"><i class="fa-solid fa-circle-notch fa-spin me-1"></i>{{ status }}</div>

        <div v-if="error" class="alert alert-danger py-2 my-2" style="font-size:13px">{{ error }}</div>
      </div>

      <div class="ai-panel-foot">
        <div class="d-flex" style="gap:6px">
          <textarea v-model="question" class="form-control form-control-sm" rows="2"
                    :placeholder="messages.length ? 'שאלת המשך...' : 'כתבו שאלה...'" @keydown.enter.exact.prevent="ask"></textarea>
          <button class="btn btn-primary btn-sm" :disabled="streaming || !question.trim()" @click="ask">
            <i class="fa-solid fa-paper-plane"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { askInsights } from './ai-insights.api';
import hasPermission from '@/core/acl/acl';

// Starter questions: the events set for everyone, a catalog-flavoured set for users who hold the
// מלאי והשכרות module (the assistant answers those with the catalog tools).
const EVENT_SUGGESTIONS = [
  'מה שלושת מקורות ההפניה שמביאים הכי הרבה סגירות?',
  'באילו חודשים יש הכי הרבה אירועים?',
  'אילו אירועים נמצאים ללא צוות וקרובים בזמן?',
  'מה אחוז הלידים שהפכו לפגישה?'
];
const CATALOG_SUGGESTIONS = [
  'מהם חמשת הפריטים הכי רווחיים בקטלוג?',
  'אילו פריטים בחוץ עכשיו ומתי הם חוזרים?',
  'כמה הכנסות היו מהשכרות ומכירות השנה, לפי חודש?',
  'אילו פריטים לא הושכרו אף פעם?'
];

export default {
  name: 'AiInsightsPanel',
  data() {
    return {
      open: false,
      question: '',
      messages: [],          // [{ role: 'user' | 'assistant', text }]
      error: '',
      streaming: false,
      status: '',
      conversationId: null,  // server-side conversation (Redis); follow-ups send it back
      abortFn: null
    };
  },
  computed: {
    hasCatalog() { return hasPermission(this.$store.getters.getUser, 'inventory'); },
    suggestions() { return this.hasCatalog ? CATALOG_SUGGESTIONS : EVENT_SUGGESTIONS; }
  },
  methods: {
    toggle() {
      this.open = !this.open;
      if (!this.open) this.stop();
    },
    // an announcement's "נסו עכשיו" (AnnouncementPopup) opens the panel from outside
    openPanel() { this.open = true; },
    useSuggestion(s) { this.question = s; this.ask(); },
    stop() {
      if (this.abortFn) { this.abortFn(); this.abortFn = null; }
      this.streaming = false;
      this.status = '';
    },
    newConversation() {
      this.stop();
      this.messages = [];
      this.conversationId = null;
      this.error = '';
      this.question = '';
    },
    finish() {
      this.streaming = false;
      this.status = '';
      this.abortFn = null;
    },
    scrollToBottom() {
      const self = this;
      this.$nextTick(() => {
        const el = self.$refs.body;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    ask() {
      const q = this.question.trim();
      if (!q || this.streaming) return;
      this.stop();
      this.error = '';
      this.messages.push({ role: 'user', text: q });
      const reply = { role: 'assistant', text: '' };
      this.messages.push(reply);
      this.question = '';
      this.streaming = true;
      this.status = 'חושב...';
      this.scrollToBottom();
      const self = this;
      this.abortFn = askInsights(
        { question: q, conversationId: this.conversationId },
        {
          onMeta(meta) { if (meta.conversationId) self.conversationId = meta.conversationId; },
          onStatus(s) { self.status = s.label || ''; },
          onDelta(text) { self.status = ''; reply.text += text; self.scrollToBottom(); },
          onDone() { self.finish(); },
          onError(msg) {
            self.error = msg;
            if (!reply.text) { const idx = self.messages.indexOf(reply); if (idx !== -1) self.messages.splice(idx, 1); }
            self.finish();
          }
        }
      );
    }
  },
  mounted() { this.$root.$on('openAiInsights', this.openPanel); },
  beforeDestroy() {
    this.$root.$off('openAiInsights', this.openPanel);
    this.stop();
  }
};
</script>

<style scoped>
.ai-fab {
  position: fixed; bottom: 24px; left: 24px; z-index: 1050;
  width: 56px; height: 56px; border-radius: 50%; border: none;
  background: linear-gradient(135deg, #7893C1, #875383); color: #fff;
  font-size: 22px; box-shadow: 0 4px 14px rgba(0,0,0,.3); cursor: pointer;
}
.ai-fab:hover { filter: brightness(1.08); }
.ai-panel {
  position: fixed; bottom: 92px; left: 24px; z-index: 1050;
  width: 380px; max-width: calc(100vw - 48px); max-height: 70vh;
  background: #fff; border-radius: 12px; display: flex; flex-direction: column;
  overflow: hidden; border: 1px solid #e3e3e3;
}
.ai-panel-head { padding: 12px 14px; background: #293B5A; color: #fff; display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.ai-new-btn { background: transparent; border: 1px solid rgba(255,255,255,.55); color: #fff; border-radius: 6px; font-size: 12px; padding: 3px 8px; white-space: nowrap; cursor: pointer; }
.ai-new-btn:disabled { opacity: .5; cursor: default; }
.ai-panel-body { padding: 12px 14px; overflow-y: auto; flex: 1; min-height: 120px; }
.ai-panel-foot { padding: 10px 12px; border-top: 1px solid #eee; background: #fafafa; }
.ai-suggestions .ai-sugg { display: block; width: 100%; text-align: right; margin-bottom: 6px; }
.ai-msg { font-size: 14px; line-height: 1.6; color: #222; margin-bottom: 10px; }
.ai-msg-user { background: #eef2f8; border-radius: 10px; padding: 7px 10px; font-size: 13.5px; }
.ai-msg-assistant { padding: 0 2px; }
.ai-status { font-size: 12.5px; color: #7893C1; margin-bottom: 8px; }
.ai-caret { animation: aiBlink 1s steps(2,start) infinite; color: #7893C1; }
@keyframes aiBlink { to { visibility: hidden; } }
</style>
